import {
  createMockFactoryImage,
  createMockFactoryImageList,
  type FactoryImage,
} from '@indutrust/factory-model';
import { http, HttpResponse, type RequestHandler } from 'msw';
import { denyFactoryMutationForViewer } from '../../+mock/factory-team-role-mutation.util';

/**
 * MSW handlers backing {@link import('../factory-image-api-client.service').FactoryImageApiClient}
 * — `${baseUrl}/factories/:factoryId/images` (CRUD) plus a permissive
 * file-upload variant that swallows multipart bodies and echoes back a
 * fully-formed {@link FactoryImage} record.
 *
 * Gallery display order is managed client-side and persisted via the main
 * factory PATCH payload (`gallery_image_ids`), so no reorder endpoint exists
 * here. The in-memory dataset is kept in server-load order for the demo.
 *
 * The dataset is a single in-memory store shared across factory ids.
 * Production scopes per `factory_id` server-side — the mock intentionally
 * does not, to keep the demo flow predictable.
 *
 * Pass the same `baseUrl` the API client receives from
 * `EnvironmentService.getApiUrl()`.
 */
export function factoryImageHandlers(baseUrl: string): RequestHandler[] {
  const url = `${baseUrl}/factories/:factoryId/images`;
  const store: FactoryImage[] = createMockFactoryImageList(3);

  const nextId = (): number =>
    store.length === 0 ? 1 : Math.max(...store.map((d) => d.id)) + 1;

  return [
    http.get(url, () => HttpResponse.json(store)),
    http.get(`${url}/:id`, ({ params }) => {
      const id = Number(params['id']);
      const found = store.find((d) => d.id === id);
      return found
        ? HttpResponse.json(found)
        : HttpResponse.json({ error: 'Not found' }, { status: 404 });
    }),
    http.post(url, async ({ request }) => {
      const denied = denyFactoryMutationForViewer();
      if (denied) {
        return denied;
      }
      const contentType = request.headers.get('content-type') ?? '';
      let imageUrl: string | undefined;

      if (contentType.includes('multipart/form-data')) {
        const form = await request.formData().catch(() => null);
        const file = form?.get('file');
        imageUrl =
          file instanceof File
            ? `https://picsum.photos/seed/${file.name}/900/600`
            : `https://picsum.photos/seed/upload-${nextId()}/900/600`;
      }

      const created = createMockFactoryImage({
        id: nextId(),
        url:
          imageUrl ?? `https://picsum.photos/seed/upload-${nextId()}/900/600`,
        position: store.length,
      });
      store.push(created);
      return HttpResponse.json(created, { status: 201 });
    }),
    http.delete(`${url}/:id`, ({ params }) => {
      const denied = denyFactoryMutationForViewer();
      if (denied) {
        return denied;
      }
      const id = Number(params['id']);
      const idx = store.findIndex((d) => d.id === id);
      if (idx !== -1) {
        store.splice(idx, 1);
      }
      return HttpResponse.json({});
    }),
  ];
}
